import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { batteryEstimatorService } from "../../services/batteryEstimator.service";
import "./BatteryEstimator.css";

const BatteryEstimator = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useSelector((state) => state.auth);

  const [form, setForm] = useState({
    batteryType: "li-ion",
    cellType: "18650",
    voltage: "",
    capacityAh: "",
    applicationType: "inverter",
    outerMaterial: "epoxy",
    buildQuality: "standard",
  });

  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [isSending, setIsSending] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState(false);

  // -------------------------------
  // 🔄 HANDLE CHANGE
  // -------------------------------
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError("");

    if (result) setResult(null);
  };

  // -------------------------------
  // 🚀 SUBMIT
  // -------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !form.batteryType ||
      !form.cellType ||
      Number(form.voltage) <= 0 ||
      Number(form.capacityAh) <= 0
    ) {
      setError("Please fill all fields correctly.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await batteryEstimatorService.estimateBattery(form);
      setResult(res);
      setEmailSuccess(false);
    } catch (err) {
      setError("Calculation failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  // -------------------------------
  // 🔄 RESET
  // -------------------------------
  const handleReset = () => {
    setForm({
      batteryType: "li-ion",
      cellType: "18650",
      voltage: "",
      capacityAh: "",
      applicationType: "inverter",
      outerMaterial: "epoxy",
      buildQuality: "standard",
    });
    setResult(null);
    setError("");
    setEmailSuccess(false);
  };

  // -------------------------------
  // 📩 SEND EMAIL
  // -------------------------------
  const handleSendEmail = async () => {
    if (!isAuthenticated || !user) {
      alert("Please log in first.");
      navigate("/auth");
      return;
    }

    setIsSending(true);

    try {
      const inquiryData = {
        userEmail: user.email,
        userName: user.displayName || "Customer",
        specs: form,
        estimation: result,
      };

      await batteryEstimatorService.sendInquiry(inquiryData);
      setEmailSuccess(true);
    } catch (err) {
      alert("Failed to send inquiry.");
    } finally {
      setIsSending(false);
    }
  };

  // -------------------------------
  // 📊 RESULT CARD (UPDATED)
  // -------------------------------
  const ResultCard = ({ data }) => (
    <div className="battery-result-box fade-in">
      <div className="result-header">
        <h3>⚡ Configuration Ready</h3>
        <span className="stock-badge">Custom Build</span>
      </div>

      <div className="cost-summary">
        <div>
          <span className="cost-label">Estimated Cost</span>
          <div className="cost-value">₹{data.totalCost.toLocaleString()}</div>
        </div>
        <button className="add-cart-btn-small">Add to Cart</button>
      </div>

      {/* Disclaimer */}
      <p className="price-disclaimer">
        * Note: This is an approximation. The real price may vary based on current market rates and final build requirements.
      </p>

      <div className="specs-summary">
        {form.applicationType} | {form.outerMaterial} | {form.buildQuality}
      </div>

      {!emailSuccess ? (
        <button
          className="send-mail-btn"
          onClick={handleSendEmail}
          disabled={isSending}
        >
          {isSending ? "Sending..." : "📩 Send Inquiry"}
        </button>
      ) : (
        <div className="success-message">✅ Inquiry Sent!</div>
      )}
    </div>
  );

  // -------------------------------
  // ℹ️ INFO PANEL
  // -------------------------------
  const InfoPanel = () => (
    <div className="info-panel fade-in">
      <div className="info-header">
        <h3>Battery Guide</h3>
        <p>Select options to see details</p>
      </div>

      <div className="info-card">
        <h4>
          Chemistry: {form.batteryType === "li-ion" ? "Lithium-Ion" : "LiFePO4"}
        </h4>
        <p>
          {form.batteryType === "li-ion"
            ? "Lightweight, high energy density."
            : "Safer, long cycle life."}
        </p>
      </div>

      <div className="info-card">
        <h4>Application: {form.applicationType}</h4>
        <p>
          {form.applicationType === "inverter" &&
            "Ideal for home backup systems."}
          {form.applicationType === "solar" &&
            "Optimized for renewable storage."}
          {form.applicationType === "ebike" &&
            "Designed for mobility and high discharge."}
        </p>
      </div>

      <div className="info-card">
        <h4>Outer Material: {form.outerMaterial}</h4>
        <p>
          {form.outerMaterial === "epoxy"
            ? "Lightweight sealed design."
            : "Strong metal enclosure for durability."}
        </p>
      </div>
    </div>
  );

  return (
    <section className="battery-estimator-wrapper">
      <div className="section-header">
        <h2 className="section-title">Build Your Own Battery</h2>
        <p className="section-subtitle">Custom packs tailored to your needs.</p>
      </div>

      <div className="estimator-container">
        {/* LEFT */}
        <div className="estimator-left">
          <form className="battery-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Battery Chemistry</label>
              <select
                name="batteryType"
                value={form.batteryType}
                onChange={handleChange}
              >
                <option value="li-ion">Lithium Ion</option>
                <option value="lifepo4">LiFePO4</option>
              </select>
            </div>

            <div className="form-group">
              <label>Cell Type</label>
              <select
                name="cellType"
                value={form.cellType}
                onChange={handleChange}
              >
                <option value="18650">18650</option>
                <option value="32700">32700</option>
                <option value="32140">32140</option>
              </select>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Voltage (V)</label>
                <input
                  type="number"
                  name="voltage"
                  value={form.voltage}
                  onChange={handleChange}
                  placeholder="48"
                />
              </div>

              <div className="form-group">
                <label>Capacity (Ah)</label>
                <input
                  type="number"
                  name="capacityAh"
                  value={form.capacityAh}
                  onChange={handleChange}
                  placeholder="100"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Application Type</label>
              <select
                name="applicationType"
                value={form.applicationType}
                onChange={handleChange}
              >
                <option value="inverter">Inverter</option>
                <option value="solar">Solar</option>
                <option value="ebike">E-Bike</option>
              </select>
            </div>

            <div className="form-group">
              <label>Outer Material</label>
              <select
                name="outerMaterial"
                value={form.outerMaterial}
                onChange={handleChange}
              >
                <option value="epoxy">Epoxy</option>
                <option value="metal">Metal</option>
              </select>
            </div>

            <div className="form-group">
              <label>Build Quality</label>
              <select
                name="buildQuality"
                value={form.buildQuality}
                onChange={handleChange}
              >
                <option value="standard">Standard</option>
                <option value="premium">Premium</option>
              </select>
            </div>

            {error && <div className="form-error">{error}</div>}

            <button type="submit" className="primary-btn" disabled={loading}>
              {loading ? "Calculating..." : "Calculate"}
            </button>

            <button type="button" onClick={handleReset} className="reset-link">
              Reset
            </button>
          </form>
        </div>

        {/* RIGHT */}
        <div className="estimator-right">
          {result ? <ResultCard data={result} /> : <InfoPanel />}
        </div>
      </div>
    </section>
  );
};

export default BatteryEstimator;